// Chemical equation balancer using Gaussian elimination

interface ParsedCompound {
  elements: Record<string, number>;
  original: string;
}

function parseCompound(compound: string): ParsedCompound {
  const elements: Record<string, number> = {};
  const cleaned = compound.trim();

  function processGroup(str: string, multiplier: number) {
    let i = 0;
    while (i < str.length) {
      if (str[i] === "(") {
        let depth = 1;
        let j = i + 1;
        while (j < str.length && depth > 0) {
          if (str[j] === "(") depth++;
          if (str[j] === ")") depth--;
          j++;
        }
        const inner = str.slice(i + 1, j - 1);
        let numStr = "";
        while (j < str.length && /\d/.test(str[j])) {
          numStr += str[j];
          j++;
        }
        const subMult = numStr ? parseInt(numStr) : 1;
        processGroup(inner, multiplier * subMult);
        i = j;
      } else if (/[A-Z]/.test(str[i])) {
        let symbol = str[i];
        i++;
        while (i < str.length && /[a-z]/.test(str[i])) {
          symbol += str[i];
          i++;
        }
        let numStr = "";
        while (i < str.length && /\d/.test(str[i])) {
          numStr += str[i];
          i++;
        }
        const count = numStr ? parseInt(numStr) : 1;
        elements[symbol] = (elements[symbol] || 0) + count * multiplier;
      } else {
        i++;
      }
    }
  }

  // Handle coefficient prefix like "2H2O"
  processGroup(cleaned, 1);

  return { elements, original: cleaned };
}

function splitEquation(equation: string): { reactants: string[]; products: string[] } | null {
  // Support -> , → , = , => as separator
  const separators = ["->", "→", "=>", "="];
  let parts: string[] | null = null;
  for (const sep of separators) {
    if (equation.includes(sep)) {
      parts = equation.split(sep);
      break;
    }
  }
  if (!parts || parts.length !== 2) return null;

  const reactants = parts[0].split("+").map((s) => s.trim()).filter(Boolean);
  const products = parts[1].split("+").map((s) => s.trim()).filter(Boolean);

  if (reactants.length === 0 || products.length === 0) return null;
  return { reactants, products };
}

function gcd(a: number, b: number): number {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) {
    [a, b] = [b, a % b];
  }
  return a;
}

export interface AtomCount {
  element: string;
  reactants: number;
  products: number;
  balanced: boolean;
}

export interface BalanceResult {
  success: boolean;
  coefficients: number[];
  balanced: string;
  steps: string[];
  reactants: string[];
  products: string[];
  atomCounts: AtomCount[];
  error?: string;
}

// Rational number arithmetic for exact balancing
type Frac = { n: number; d: number }; // n/d, d > 0
function fnorm(n: number, d: number): Frac {
  if (d < 0) { n = -n; d = -d; }
  const g = gcd(n, d) || 1;
  return { n: n / g, d: d / g };
}
const F = {
  of: (n: number): Frac => ({ n, d: 1 }),
  add: (a: Frac, b: Frac): Frac => fnorm(a.n * b.d + b.n * a.d, a.d * b.d),
  sub: (a: Frac, b: Frac): Frac => fnorm(a.n * b.d - b.n * a.d, a.d * b.d),
  mul: (a: Frac, b: Frac): Frac => fnorm(a.n * b.n, a.d * b.d),
  div: (a: Frac, b: Frac): Frac => fnorm(a.n * b.d, a.d * b.n),
  isZero: (a: Frac) => a.n === 0,
  neg: (a: Frac): Frac => ({ n: -a.n, d: a.d }),
};

export function balanceEquation(equation: string): BalanceResult {
  const steps: string[] = [];
  const split = splitEquation(equation);
  if (!split) {
    return { success: false, coefficients: [], balanced: "", steps: [], reactants: [], products: [], atomCounts: [], error: "Invalid equation format. Use format: H2 + O2 -> H2O" };
  }

  const { reactants, products } = split;
  steps.push(`Identified ${reactants.length} reactant(s) and ${products.length} product(s)`);

  const allCompounds = [...reactants, ...products];
  const parsed = allCompounds.map(parseCompound);

  const elementSet = new Set<string>();
  parsed.forEach((p) => Object.keys(p.elements).forEach((e) => elementSet.add(e)));
  const elementList = Array.from(elementSet);

  steps.push(`Elements involved: ${elementList.join(", ")}`);

  const numCompounds = allCompounds.length;
  const numElements = elementList.length;

  // Build composition matrix in exact rationals: rows = elements, cols = compounds.
  // Reactants positive, products negative. Solve A x = 0 for nontrivial x.
  const M: Frac[][] = [];
  for (let i = 0; i < numElements; i++) {
    const row: Frac[] = [];
    for (let j = 0; j < numCompounds; j++) {
      let val = parsed[j].elements[elementList[i]] || 0;
      if (j >= reactants.length) val = -val;
      row.push(F.of(val));
    }
    M.push(row);
  }

  steps.push("Built composition matrix and reduced to row-echelon form");

  // Reduced row-echelon form (RREF) over rationals.
  const rows = numElements;
  const cols = numCompounds;
  const pivotColForRow: number[] = [];
  let r = 0;
  for (let c = 0; c < cols && r < rows; c++) {
    // find pivot
    let pivot = -1;
    for (let i = r; i < rows; i++) {
      if (!F.isZero(M[i][c])) { pivot = i; break; }
    }
    if (pivot === -1) continue;
    [M[r], M[pivot]] = [M[pivot], M[r]];
    // normalize pivot row
    const pv = M[r][c];
    for (let j = c; j < cols; j++) M[r][j] = F.div(M[r][j], pv);
    // eliminate other rows
    for (let i = 0; i < rows; i++) {
      if (i === r) continue;
      const factor = M[i][c];
      if (F.isZero(factor)) continue;
      for (let j = c; j < cols; j++) {
        M[i][j] = F.sub(M[i][j], F.mul(factor, M[r][j]));
      }
    }
    pivotColForRow.push(c);
    r++;
  }

  // Find free columns. Need exactly one for a unique (up to scale) solution.
  const pivotCols = new Set(pivotColForRow);
  const freeCols: number[] = [];
  for (let c = 0; c < cols; c++) if (!pivotCols.has(c)) freeCols.push(c);

  if (freeCols.length === 0) {
    return { success: false, coefficients: [], balanced: "", steps, reactants, products, atomCounts: [], error: "No nontrivial solution - equation cannot be balanced." };
  }

  // Use the LAST free column (typically the last product) set to 1.
  const free = freeCols[freeCols.length - 1];
  const solution: Frac[] = new Array(cols).fill(null).map(() => F.of(0));
  solution[free] = F.of(1);
  // Other free vars = 0 (already)
  // For each pivot row: pivot var = -sum(other coeffs * their values)
  for (let i = 0; i < pivotColForRow.length; i++) {
    const pc = pivotColForRow[i];
    let sum = F.of(0);
    for (let j = pc + 1; j < cols; j++) {
      if (!F.isZero(M[i][j]) && !F.isZero(solution[j])) {
        sum = F.add(sum, F.mul(M[i][j], solution[j]));
      }
    }
    solution[pc] = F.neg(sum);
  }

  // Multiply through by LCM of denominators to make integers
  let L = 1;
  for (const f of solution) L = (L * f.d) / gcd(L, f.d);
  let coefficients = solution.map((f) => Math.round((f.n * L) / f.d));

  // Flip sign if needed so all positive
  if (coefficients.some((c) => c < 0) && coefficients.every((c) => c <= 0)) {
    coefficients = coefficients.map((c) => -c);
  }
  if (coefficients.some((c) => c < 0)) {
    // mixed signs: flip all (since x and -x both span null space)
    coefficients = coefficients.map((c) => -c);
  }
  if (coefficients.some((c) => c <= 0)) {
    return { success: false, coefficients: [], balanced: "", steps, reactants, products, atomCounts: [], error: "Could not find positive integer coefficients." };
  }

  // Reduce by GCD
  let commonGcd = coefficients[0];
  for (let i = 1; i < coefficients.length; i++) commonGcd = gcd(commonGcd, coefficients[i]);
  if (commonGcd > 1) coefficients = coefficients.map((c) => c / commonGcd);

  steps.push(`Coefficients: ${coefficients.join(", ")}`);

  // Build balanced equation string
  const reactantParts = reactants.map((r, i) => `${coefficients[i] > 1 ? coefficients[i] : ""}${r}`);
  const productParts = products.map((p, i) => `${coefficients[reactants.length + i] > 1 ? coefficients[reactants.length + i] : ""}${p}`);
  const balanced = `${reactantParts.join(" + ")} → ${productParts.join(" + ")}`;

  // Verify
  const verify: Record<string, { left: number; right: number }> = {};
  elementList.forEach((el) => {
    verify[el] = { left: 0, right: 0 };
    reactants.forEach((_, i) => {
      verify[el].left += (parsed[i].elements[el] || 0) * coefficients[i];
    });
    products.forEach((_, i) => {
      verify[el].right += (parsed[reactants.length + i].elements[el] || 0) * coefficients[reactants.length + i];
    });
  });

  const isBalanced = Object.values(verify).every((v) => v.left === v.right);
  if (isBalanced) {
    steps.push("✓ Verified: all elements balanced on both sides");
  } else {
    steps.push("⚠ Verification: some elements may not balance perfectly");
  }

  const atomCounts: AtomCount[] = Object.entries(verify).map(([el, v]) => ({
    element: el,
    reactants: v.left,
    products: v.right,
    balanced: v.left === v.right,
  }));

  return {
    success: isBalanced,
    coefficients,
    balanced,
    steps,
    reactants,
    products,
    atomCounts,
  };
}
